import type { AppStore } from "@/store/AppStore";
import { CreateAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/CreateAudioRecordingOperation";
import { DeleteAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/DeleteAudioRecordingOperation";
import { TrashAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/TrashAudioRecordingOperation";
import { RestoreAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/RestoreAudioRecordingOperation";
import { MarkStartedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedLocalAudioRecordingOperation";
import { MarkCompletedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedLocalAudioRecordingOperation";
import { MarkErrorStartingLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorStartingLocalAudioRecordingOperation";
import { MarkErrorResumingLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorResumingLocalAudioRecordingOperation";
import { MarkPausedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkPausedLocalAudioRecordingOperation";
import { MarkResumedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkResumedLocalAudioRecordingOperation";
import { MarkStartedUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedUploadingAudioRecordingOperation";
import { MarkCompletedUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedUploadingAudioRecordingOperation";
import { MarkErrorUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorUploadingAudioRecordingOperation";
import { MarkTranscriptionDisconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkTranscriptionDisconnectedAudioRecordingOperation";
import { MarkTranscriptionReconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkTranscriptionReconnectedAudioRecordingOperation";
import { MarkTranscriptionCompromisedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkTranscriptionCompromisedAudioRecordingOperation";
import { MarkVoiceDisconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkVoiceDisconnectedAudioRecordingOperation";
import { MarkVoiceReconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkVoiceReconnectedAudioRecordingOperation";
import { MarkStartedProcessingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedProcessingAudioRecordingOperation";
import { MarkErrorProcessingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorProcessingAudioRecordingOperation";
import { MarkCompletedProcessingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedProcessingAudioRecordingOperation";
import {
  AudioRecordingObservable,
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";
import type { Uuid } from "@/domains/global/identifiers";

import { BaseSyncModelRepository } from "../base-repository";
import { LocalAudioRecordingBlobsRepository } from "./local-audio-recording-blobs";
import { LocalAudioRecordingTranscriptTurnsRepository } from "./local-audio-recording-transcript-turns";
import { uuidModule } from "@/modules/uuid";
import { clientEnvModule } from "@/modules/client-env";
import { AudioRecordingRecord } from "@/domains/dexie-db/models/audio-recording/audio-recording";
import type { IAudioRecordingAppEventOperation } from "@/store/sync/operations/types";

type RepositoryDependencies = {
  localAudioRecordingBlobs: LocalAudioRecordingBlobsRepository;
  localAudioRecordingTranscriptTurns: LocalAudioRecordingTranscriptTurnsRepository;
};

type UpdateAudioRecordingData = {
  associatedNoteId?: Uuid | null;
  recordingStatus?: AudioRecordingRecordingStatus;
  uploadStatus?: AudioRecordingUploadStatus;
  processingStatus?: AudioRecordingProcessingStatus;
};

export class AudioRecordingRepository extends BaseSyncModelRepository<
  AudioRecordingRecord,
  AudioRecordingObservable
> {
  private readonly store: AppStore;
  private readonly repositories: RepositoryDependencies;

  constructor({ store, repositories }: { store: AppStore; repositories: RepositoryDependencies }) {
    super({ store });
    this.store = store;
    this.repositories = repositories;
  }

  async upsert({
    id,
    data,
  }: {
    id: Uuid;
    data: UpdateAudioRecordingData;
  }): Promise<Pick<AudioRecordingObservable, "id">> {
    const existing = await this.getById({ id });

    if (existing) {
      return { id };
    }

    return this.create({ id, data });
  }

  async create({
    id,
    data,
  }: {
    id?: Uuid | null;
    data: UpdateAudioRecordingData;
  }): Promise<Pick<AudioRecordingObservable, "id">> {
    const audioRecordingId = id ?? uuidModule.generate();

    /**
     * Note that we specifically use the client group ID here, rather than the client ID.
     * This is because audio-recordings matter per-device, rather than per-tab.
     */
    const { clientGroupId } = await clientEnvModule.clientGroupId();

    await new CreateAudioRecordingOperation({
      store: this.store,
      payload: {
        id: audioRecordingId,
        recording_status: data.recordingStatus ?? AudioRecordingRecordingStatus.INITIAL,
        upload_status: data.uploadStatus ?? AudioRecordingUploadStatus.INITIAL,
        processing_status: data.processingStatus ?? AudioRecordingProcessingStatus.INITIAL,
        associated_note_id: data.associatedNoteId ?? null,
        associated_client_id: clientGroupId,
        associated_platform_kind: clientEnvModule.platformKind(),
        value_json: {},
      },
    }).execute();

    return {
      id: audioRecordingId,
    };
  }

  async markAsRecordingStarted({
    id,
    recallSdkUploadId,
  }: {
    id: Uuid;
    recallSdkUploadId?: string | null;
  }): Promise<void> {
    await new MarkStartedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { recall_sdk_upload_id: recallSdkUploadId }),
    }).execute();
  }

  async markAsRecordingCompleted({ id }: { id: Uuid }): Promise<void> {
    await new MarkCompletedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsRecordingErrored({
    id,
    errorMessage,
  }: {
    id: Uuid;
    errorMessage?: string | null;
  }): Promise<void> {
    await new MarkErrorStartingLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { error_message: errorMessage }),
    }).execute();
  }

  async markAsPaused({ id }: { id: Uuid }): Promise<void> {
    await new MarkPausedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsResumed({
    id,
    recallSdkUploadId,
  }: {
    id: Uuid;
    recallSdkUploadId?: string | null;
  }): Promise<void> {
    await new MarkResumedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { recall_sdk_upload_id: recallSdkUploadId }),
    }).execute();
  }

  async markAsResumingErrored({
    id,
    errorMessage,
  }: {
    id: Uuid;
    errorMessage?: string | null;
  }): Promise<void> {
    await new MarkErrorResumingLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { error_message: errorMessage }),
    }).execute();
  }

  async markAsUploading({ id }: { id: Uuid }): Promise<void> {
    await new MarkStartedUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsUploadCompleted({ id }: { id: Uuid }): Promise<void> {
    await new MarkCompletedUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsUploadErrored({
    id,
    errorMessage,
  }: {
    id: Uuid;
    errorMessage?: string | null;
  }): Promise<void> {
    await new MarkErrorUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { error_message: errorMessage }),
    }).execute();
  }

  async markTranscriptionDisconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkTranscriptionDisconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markTranscriptionReconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkTranscriptionReconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markTranscriptionCompromised({ id }: { id: Uuid }): Promise<void> {
    await new MarkTranscriptionCompromisedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markVoiceDisconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkVoiceDisconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markVoiceReconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkVoiceReconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  private buildAppEventPayload(
    id: Uuid,
    properties?: Record<string, unknown>
  ): IAudioRecordingAppEventOperation["payload"] {
    // Filter out null/undefined values from properties
    const filteredProperties = properties
      ? Object.fromEntries(Object.entries(properties).filter(([, v]) => v != null))
      : undefined;

    return {
      schema_version: 1,
      id,
      occurred_at: new Date().toISOString(),
      properties:
        filteredProperties && Object.keys(filteredProperties).length > 0
          ? filteredProperties
          : undefined,
    };
  }

  async markAsProcessingInProgress({ id }: { id: Uuid }): Promise<void> {
    await new MarkStartedProcessingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  /**
   * Ideally this is set by the server, but there are cases where we know it is completed client-side
   * and can set it here.
   *
   * (eg. after the user accepts a voice suggestion for an in-progress recording.)
   */
  async markAsProcessingCompleted({ id }: { id: Uuid }): Promise<void> {
    await new MarkCompletedProcessingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsProcessingErrored({ id }: { id: Uuid }): Promise<void> {
    await new MarkErrorProcessingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async delete({ id }: { id: Uuid }): Promise<void> {
    await new DeleteAudioRecordingOperation({
      store: this.store,
      payload: { id },
    }).execute();

    await Promise.all([
      this.repositories.localAudioRecordingBlobs.deleteAllByRecordingId({
        associatedAudioRecordingId: id,
      }),
      this.repositories.localAudioRecordingTranscriptTurns.deleteAllByRecordingId({
        associatedAudioRecordingId: id,
      }),
    ]);
  }

  async getById({ id }: { id: Uuid }): Promise<AudioRecordingRecord | null> {
    const localModel = await this.store.audioRecordings.localTable.get(id);
    if (!localModel) return null;

    return {
      id,
      data: localModel.model_data,
    };
  }

  async trash({ id }: { id: Uuid }): Promise<void> {
    await new TrashAudioRecordingOperation({
      store: this.store,
      payload: { id },
    }).execute();
  }

  async restore({ id }: { id: Uuid }): Promise<void> {
    await new RestoreAudioRecordingOperation({
      store: this.store,
      payload: { id },
    }).execute();
  }

  async hasAnyForNote({ noteId }: { noteId: Uuid }): Promise<boolean> {
    const table = this.store.audioRecordings.localTable;
    const { clientGroupId } = await clientEnvModule.clientGroupId();

    const latestRecording = await table
      .where("associated_note_id")
      .equals(noteId)
      .filter(row => row.trashed_at === null)
      .filter(row => row.associated_client_id === clientGroupId)
      .sortBy("locally_created_at")
      .then(records => records.at(-1));

    return Boolean(latestRecording);
  }

  async isInRecordingState({ id }: { id: Uuid }): Promise<boolean> {
    const recording = await this.getById({ id });
    return recording?.data.recording_status === AudioRecordingRecordingStatus.IN_PROGRESS;
  }
}
